import {TOWN_REPORTS} from '../data/townReportNavigation';
import {useSharedFilter} from '../../../shared/navigation/useSharedReport';
import { TableSearchScope, SectionTableSearch } from "../../../shared/ui/table/TableSearchScope";
// src/features/townBudget/components/SummaryOfRevenues.jsx

/**
 * @file SummaryOfRevenues.jsx
 * @description Displays a collapsible summary table of revenue and transfer categories
 * (taxes, licenses, intergovernmental, charges, other, transfers) for the selected fiscal year.
 * Renders inner section headers (e.g. "Tax Revenues", "HousAuth In-Lieu Tax") and category totals
 * to match the PDF layout.
 *
 * Enhanced:
 * - Adds 3 line charts (Approved, Actual, Approved vs Actual)
 * - Adds pie chart for category breakdown (Tax/Licenses/Intergov/Charges/Other/Transfers)
 */

import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { CommonTable } from "../../../shared/ui/table/CommonTable.jsx";
import { buildRevenueColumns } from "../../../budget/model/columns/columnBuilders.js";
import { fetchRevenueData } from "../data/fetchRevenueData.js";
import { INTERGOV_SECTION_ORDER } from "../../../shared/constants/tableOrder.js";
import {
  applySectionRowOrder,
  createSectionChangeHandler,
  buildSingleSectionDisplayData,
  buildIntergovDisplayData,
} from "../utils/revenueTableUtils.js";

// Charts UI (same library used by SummaryOfRevenuesAndExpenditures)
import { PieChartWithToggles } from "../../../shared/ui/chart/PieChartWithToggles.jsx";
import { HeaderToggleButton } from "../../../shared/ui/overlay/HeaderToggleButton.jsx";
import { LineChartWithToggles } from "../../../shared/ui/chart/LineChartWithToggles.jsx";

// ✅ Reuse the SAME summary utils used by SummaryOfRevenuesAndExpenditures
import {
  REVENUE_CATEGORY_CONFIG,
  computeIncreaseFields,
  aggregateFromSections,
  rowHasAnyNumeric,
} from "../utils/summaryOfRevenuesAndExpendituresUtils.js";

function fyLabelFromStartYear(startYear) {
  if (!Number.isFinite(startYear)) return "";
  return `${startYear}–${startYear + 1}`;
}

function SummaryOfRevenues({
  numericYears = [],
  availableYears = [],
  setData,
  isEditing,
}) {
  const [collapsed, setCollapsed] = useState(true);

  // Split initial loading vs updating so we don't unmount tables on year change
  const [loading, setLoading] = useState(false); // "no data yet"
  const [isUpdating, setIsUpdating] = useState(false); // "data already shown; swapping year"

  // Local year state: this dropdown ONLY affects this component.
  const [tableYear, setTableYear] = useSharedFilter("SummaryOfRevenues.jsx:SummaryOfRevenues:tableYear", null);

  const [taxSections, setTaxSections] = useState([]);
  const [licenseSections, setLicenseSections] = useState([]);
  const [intergovSections, setIntergovSections] = useState([]);
  const [chargesSections, setChargesSections] = useState([]);
  const [otherSections, setOtherSections] = useState([]);
  const [transfersSections, setTransfersSections] = useState([]);

  // ✅ only show years that have data for all required base columns
  const [validYears, setValidYears] = useState([]);

  // Cache results per year so switching back feels instant
  const yearCacheRef = useRef(new Map()); // Map<number, { taxSections, licenseSections, intergovSections, chargesSections, otherSections, transfersSections }>

  const hasAnyData =
    taxSections.length ||
    licenseSections.length ||
    intergovSections.length ||
    chargesSections.length ||
    otherSections.length ||
    transfersSections.length;

  // Initialize to latest available year.
  useEffect(() => {
    if (availableYears.length > 0) {
      setTableYear.initialize((prev) => {
        const latest = availableYears[availableYears.length - 1];
        return prev && availableYears.includes(prev) ? prev : latest;
      });
      return;
    }

    // Fallback: if no availableYears passed, derive from numericYears
    if (numericYears.length > 0) {
      const latest = Math.max(...numericYears);
      setTableYear.initialize((prev) => (typeof prev === "number" ? prev : latest));
    }
  }, [availableYears, numericYears]);

  // ---- column metadata ----
  const fallbackYear = new Date().getFullYear() + 1;
  const currentYear =
    tableYear ??
    (numericYears.length ? Math.max(...numericYears) : fallbackYear);

  const { columns } = useMemo(() => buildRevenueColumns(currentYear), [
    currentYear,
  ]);

  const yearKey = (offset, type) => `y${offset}_${type}`;

  // For “completeness”, require all base revenue columns (except label)
  // Keys are stable: y2_adopted, y2_actual, y1_adopted, y0_adopted
  const requiredBaseKeys = useMemo(() => {
    const sampleYear =
      availableYears?.[availableYears.length - 1] ??
      new Date().getFullYear() + 1;

    const { baseColumns: sampleBase } = buildRevenueColumns(sampleYear);

    return (sampleBase || [])
      .map((c) => c?.key)
      .filter((k) => k && k !== "label");
  }, [availableYears]);

  const isFiniteNumber = (v) => typeof v === "number" && Number.isFinite(v);

  // ✅ Build TOTAL REVENUES row using the SAME logic as SummaryOfRevenuesAndExpenditures
  // (critically: only set a column if we actually saw a numeric value)
  const computeTotalRevenuesRowForYear = useCallback((year, sectionsObj) => {
    const { columns: yearColumns } = buildRevenueColumns(year);

    const revenueSectionsByKey = {
      taxes: sectionsObj?.taxSections || [],
      licensesAndPermits: sectionsObj?.licenseSections || [],
      intergovernmental: sectionsObj?.intergovSections || [],
      chargesForServices: sectionsObj?.chargesSections || [],
      otherRevenues: sectionsObj?.otherSections || [],
      transfersIn: sectionsObj?.transfersSections || [],
    };

    const categoryAggRows = [];

    for (const cfg of REVENUE_CATEGORY_CONFIG) {
      const sectionsForCat = revenueSectionsByKey[cfg.key] || [];
      const aggRow = aggregateFromSections(
        sectionsForCat,
        yearColumns,
        cfg.label
      );
      if (rowHasAnyNumeric(aggRow, yearColumns)) categoryAggRows.push(aggRow);
    }

    const totalRow = (() => {
      const row = { label: "TOTAL REVENUES", bold: true };
      const yearCols = yearColumns.filter(
        (c) => !["label", "increase", "percentIncrease"].includes(c.key)
      );

      yearCols.forEach((col) => {
        let sum = 0;
        let hasVal = false;

        for (const r of categoryAggRows) {
          const v = r?.[col.key];
          if (typeof v === "number" && Number.isFinite(v)) {
            sum += v;
            hasVal = true;
          }
        }

        if (hasVal) row[col.key] = sum;
      });

      return computeIncreaseFields(row);
    })();

    return totalRow;
  }, []);

  // Build category aggregate rows for a year (used for pie)
  const computeCategoryAggRowsForYear = useCallback((year, sectionsObj) => {
    const { columns: yearColumns } = buildRevenueColumns(year);

    const revenueSectionsByKey = {
      taxes: sectionsObj?.taxSections || [],
      licensesAndPermits: sectionsObj?.licenseSections || [],
      intergovernmental: sectionsObj?.intergovSections || [],
      chargesForServices: sectionsObj?.chargesSections || [],
      otherRevenues: sectionsObj?.otherSections || [],
      transfersIn: sectionsObj?.transfersSections || [],
    };

    const rows = [];
    for (const cfg of REVENUE_CATEGORY_CONFIG) {
      const sectionsForCat = revenueSectionsByKey[cfg.key] || [];
      const aggRow = aggregateFromSections(
        sectionsForCat,
        yearColumns,
        cfg.label
      );
      if (rowHasAnyNumeric(aggRow, yearColumns)) rows.push(aggRow);
    }
    return rows;
  }, []);

  const isYearComplete = useCallback(
    (year, sectionsObj) => {
      const totalRow = computeTotalRevenuesRowForYear(year, sectionsObj);
      if (!totalRow) return false;

      // ✅ every required comparison column must be present as a finite number
      return requiredBaseKeys.every((k) => isFiniteNumber(totalRow?.[k]));
    },
    [requiredBaseKeys, computeTotalRevenuesRowForYear]
  );

  // ---- Fetch + cache sections for a given year ----
  const fetchAndCacheYearSections = useCallback(async (year) => {
    const { baseColumns: yrBaseCols } = buildRevenueColumns(year);

    const [taxes, licenses, intergov, charges, other, transfers] =
      await Promise.all([
        fetchRevenueData("taxes", yrBaseCols),
        fetchRevenueData("licensesAndPermits", yrBaseCols),
        fetchRevenueData("intergovernmental", yrBaseCols),
        fetchRevenueData("chargesForServices", yrBaseCols),
        fetchRevenueData("otherRevenues", yrBaseCols),
        fetchRevenueData("transfersIn", yrBaseCols),
      ]);

    const next = {
      taxSections: applySectionRowOrder(taxes.sections, "taxes"),
      licenseSections: applySectionRowOrder(
        licenses.sections,
        "licensesAndPermits"
      ),
      intergovSections: applySectionRowOrder(
        intergov.sections,
        "intergovernmental"
      ),
      chargesSections: applySectionRowOrder(
        charges.sections,
        "chargesForServices"
      ),
      otherSections: applySectionRowOrder(other.sections, "otherRevenues"),
      transfersSections: applySectionRowOrder(
        transfers.sections,
        "transfersIn"
      ),
    };

    yearCacheRef.current.set(year, next);
    return next;
  }, []);

  // ✅ compute validYears (only when expanded)
  useEffect(() => {
    if (collapsed) return;

    if (!availableYears.length) {
      setValidYears([]);
      return;
    }

    let cancelled = false;

    (async () => {
      const nextValid = [];

      for (const y of availableYears) {
        if (cancelled) return;

        // Use cache if present; otherwise fetch+cache
        let sectionsObj = yearCacheRef.current.get(y);
        if (!sectionsObj) {
          try {
            sectionsObj = await fetchAndCacheYearSections(y);
          } catch (e) {
            continue; // treat as invalid
          }
        }

        try {
          if (isYearComplete(y, sectionsObj)) nextValid.push(y);
        } catch (e) {
          // invalid
        }
      }

      if (cancelled) return;

      setValidYears(nextValid);

      // If current selection is invalid, bump to latest valid year
      if (nextValid.length) {
        setTableYear.initialize((prev) =>
          prev && nextValid.includes(prev)
            ? prev
            : nextValid[nextValid.length - 1]
        );
      } else {
        // No valid years: fall back to latest available (rare)
        setTableYear.initialize((prev) => {
          const latest = availableYears[availableYears.length - 1];
          return prev && availableYears.includes(prev) ? prev : latest;
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [collapsed, availableYears, fetchAndCacheYearSections, isYearComplete]);

  const yearOptionsToShow = useMemo(
    () => (validYears.length ? validYears : availableYears),
    [validYears, availableYears]
  );

  // ---- fetch revenue data for all categories (per selected year) ----
  useEffect(() => {
    if (collapsed) return;
    if (!tableYear) return;

    // Serve cached result immediately (no spinner, no fetch)
    const cached = yearCacheRef.current.get(tableYear);
    if (cached) {
      setTaxSections(cached.taxSections);
      setLicenseSections(cached.licenseSections);
      setIntergovSections(cached.intergovSections);
      setChargesSections(cached.chargesSections);
      setOtherSections(cached.otherSections);
      setTransfersSections(cached.transfersSections);
      setLoading(false);
      setIsUpdating(false);
      return;
    }

    if (hasAnyData) setIsUpdating(true);
    else setLoading(true);

    let cancelled = false;

    (async () => {
      try {
        const next = await fetchAndCacheYearSections(tableYear);
        if (cancelled) return;

        setTaxSections(next.taxSections);
        setLicenseSections(next.licenseSections);
        setIntergovSections(next.intergovSections);
        setChargesSections(next.chargesSections);
        setOtherSections(next.otherSections);
        setTransfersSections(next.transfersSections);

        setLoading(false);
        setIsUpdating(false);
      } catch (err) {
        console.error("[SummaryOfRevenues] Error fetching revenue summary:", err);
        if (!cancelled) {
          setLoading(false);
          setIsUpdating(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [collapsed, tableYear, hasAnyData, fetchAndCacheYearSections]);

  // ---- Edit handlers ----
  const handleTaxChange = createSectionChangeHandler(setTaxSections);
  const handleLicenseChange = createSectionChangeHandler(setLicenseSections);
  const handleIntergovChange = createSectionChangeHandler(setIntergovSections);
  const handleChargesChange = createSectionChangeHandler(setChargesSections);
  const handleOtherChange = createSectionChangeHandler(setOtherSections);
  const handleTransfersChange = createSectionChangeHandler(setTransfersSections);

  // ---- Display data ----
  const taxDisplayData = useMemo(
    () => buildSingleSectionDisplayData(taxSections, columns, "TOTAL TAXES"),
    [taxSections, columns]
  );

  const licenseDisplayData = useMemo(
    () =>
      buildSingleSectionDisplayData(
        licenseSections,
        columns,
        "TOTAL LICENSES & PERMITS"
      ),
    [licenseSections, columns]
  );

  const intergovDisplayData = useMemo(
    () => buildIntergovDisplayData(intergovSections, columns),
    [intergovSections, columns]
  );

  const chargesDisplayData = useMemo(
    () =>
      buildSingleSectionDisplayData(
        chargesSections,
        columns,
        "TOTAL CHARGES FOR SERVICES"
      ),
    [chargesSections, columns]
  );

  const otherDisplayData = useMemo(
    () =>
      buildSingleSectionDisplayData(
        otherSections,
        columns,
        "TOTAL OTHER REVENUES"
      ),
    [otherSections, columns]
  );

  const transfersDisplayData = useMemo(
    () =>
      buildSingleSectionDisplayData(
        transfersSections,
        columns,
        "TOTAL TRANSFERS IN"
      ),
    [transfersSections, columns]
  );

  const grandTotalRow = useMemo(() => {
    const sectionTotals = [
      taxDisplayData,
      licenseDisplayData,
      intergovDisplayData,
      chargesDisplayData,
      otherDisplayData,
      transfersDisplayData,
    ]
      .map((section) =>
        section.find(
          (row) =>
            typeof row.label === "string" &&
            row.label.toUpperCase().startsWith("TOTAL ")
        )
      )
      .filter(Boolean);

    if (sectionTotals.length === 0) return null;

    const totalRow = { label: "GRAND TOTAL REVENUES & TRANSFERS" };

    columns.forEach((col) => {
      const key = col.key;
      if (key === "label" || key === "increase" || key === "percentIncrease")
        return;

      const sawAny = sectionTotals.some((r) => isFiniteNumber(r?.[key]));
      if (!sawAny) return;

      const sum = sectionTotals.reduce((acc, row) => {
        const val = row[key];
        return typeof val === "number" ? acc + val : acc;
      }, 0);

      totalRow[key] = sum;
    });

    const prev = totalRow.y1_adopted;
    const curr = totalRow.y0_adopted;

    totalRow.increase =
      typeof prev === "number" && typeof curr === "number" ? curr - prev : null;

    totalRow.percentIncrease =
      typeof prev === "number" && prev !== 0 && typeof curr === "number"
        ? ((curr - prev) / prev) * 100
        : null;

    return totalRow;
  }, [
    columns,
    taxDisplayData,
    licenseDisplayData,
    intergovDisplayData,
    chargesDisplayData,
    otherDisplayData,
    transfersDisplayData,
  ]);

  // ---------------------------
  // Charts + Pie UI state
  // ---------------------------
  const [showApprovedTotalsChart, setShowApprovedTotalsChart] = useState(false);
  const [showActualTotalsChart, setShowActualTotalsChart] = useState(false);
  const [showDiffTotalsChart, setShowDiffTotalsChart] = useState(false);

  const [showRevenuePie, setShowRevenuePie] = useState(false);
  const [pieColKey, setPieColKey] = useSharedFilter("SummaryOfRevenues.jsx:SummaryOfRevenues:pieColKey", "y0_adopted");

  const [isChartUpdating, setIsChartUpdating] = useState(false);

  // Ensure pieColKey stays valid for the currently-built columns
  const pieColOptions = useMemo(() => {
    const numericCols = (columns || []).filter(
      (c) => c?.key && !["label", "increase", "percentIncrease"].includes(c.key)
    );

    return numericCols.map((c) => ({
      key: c.key,
      label: typeof c.label === "string" ? c.label : c.key,
    }));
  }, [columns]);

  useEffect(() => {
    if (!pieColOptions.length) return;

    setPieColKey.initialize((prev) => {
      const preferred =
        pieColOptions.find((o) => o.key === "y0_adopted")?.key ||
        pieColOptions[pieColOptions.length - 1].key;

      if (prev && pieColOptions.some((o) => o.key === prev)) return prev;
      return preferred;
    });
  }, [pieColOptions]);

  // Warm cache for charts when any chart is opened
  const ensureYearsCached = useCallback(
    async (yearsToEnsure) => {
      const list = Array.isArray(yearsToEnsure) ? yearsToEnsure : [];
      if (!list.length) return;

      const missing = list.filter((y) => !yearCacheRef.current.get(y));
      if (!missing.length) return;

      setIsChartUpdating(true);
      try {
        for (const y of missing) {
          await fetchAndCacheYearSections(y);
        }
      } finally {
        setIsChartUpdating(false);
      }
    },
    [fetchAndCacheYearSections]
  );

  const baseYearsForCharts = useMemo(
    () => (yearOptionsToShow.length ? yearOptionsToShow : availableYears),
    [yearOptionsToShow, availableYears]
  );

  useEffect(() => {
    if (collapsed) return;

    const wantsAnyChart =
      showApprovedTotalsChart || showActualTotalsChart || showDiffTotalsChart;

    if (!wantsAnyChart) return;

    const plus2Years = baseYearsForCharts.map((y) => y + 2);
    const toEnsure = Array.from(new Set([...baseYearsForCharts, ...plus2Years]));

    ensureYearsCached(toEnsure);
  }, [
    collapsed,
    showApprovedTotalsChart,
    showActualTotalsChart,
    showDiffTotalsChart,
    baseYearsForCharts,
    ensureYearsCached,
  ]);

  // Approved totals points (TOTAL REVENUES.y0_adopted) by FY start year
  const revenueApprovedTotalsPoints = useMemo(() => {
    const baseYears = baseYearsForCharts;
    if (!baseYears.length) return [];

    const pts = [];
    baseYears.forEach((startYear) => {
      const sectionsObj = yearCacheRef.current.get(startYear);
      if (!sectionsObj) return;

      const totalRow = computeTotalRevenuesRowForYear(startYear, sectionsObj);
      const v = totalRow?.y0_adopted;

      if (typeof v === "number" && Number.isFinite(v))
        pts.push({ year: startYear, total: v });
    });

    pts.sort((a, b) => a.year - b.year);
    return pts;
  }, [baseYearsForCharts, computeTotalRevenuesRowForYear]);

  // Actual totals points using the same anchor logic:
  // actual for FY Y is stored in (Y+2).y2_actual
  const revenueActualTotalsPoints = useMemo(() => {
    const baseYears = baseYearsForCharts;
    if (!baseYears.length) return [];

    const pts = [];
    baseYears.forEach((fyStartYear) => {
      const anchorYear = fyStartYear + 2;
      const sectionsObj = yearCacheRef.current.get(anchorYear);
      if (!sectionsObj) return;

      const totalRow = computeTotalRevenuesRowForYear(anchorYear, sectionsObj);
      const v = totalRow?.y2_actual;

      if (typeof v === "number" && Number.isFinite(v))
        pts.push({ year: fyStartYear, total: v });
    });

    pts.sort((a, b) => a.year - b.year);
    return pts;
  }, [baseYearsForCharts, computeTotalRevenuesRowForYear]);

  const revenueApprovedTotalsChart = useMemo(() => {
    if (!revenueApprovedTotalsPoints.length) return { labels: [], series: [] };

    const labels = revenueApprovedTotalsPoints.map((p) =>
      fyLabelFromStartYear(p.year)
    );
    const data = revenueApprovedTotalsPoints.map((p) => p.total);

    return {
      labels,
      series: [
        {
          key: "totalApprovedRevenues",
          label: "Total Approved Revenues",
          data,
          group: "revenues",
          defaultVisible: true,
          valueType: "currency",
        },
      ],
    };
  }, [revenueApprovedTotalsPoints]);

  const revenueActualTotalsChart = useMemo(() => {
    if (!revenueActualTotalsPoints.length) return { labels: [], series: [] };

    const labels = revenueActualTotalsPoints.map((p) =>
      fyLabelFromStartYear(p.year)
    );
    const data = revenueActualTotalsPoints.map((p) => p.total);

    return {
      labels,
      series: [
        {
          key: "totalActualRevenues",
          label: "Total Actual Revenues",
          data,
          group: "revenues",
          defaultVisible: true,
          valueType: "currency",
        },
      ],
    };
  }, [revenueActualTotalsPoints]);

  const revenueDiffTotalsChart = useMemo(() => {
    if (!revenueApprovedTotalsPoints.length || !revenueActualTotalsPoints.length)
      return { labels: [], series: [], groups: [] };

    const approvedByYear = new Map(
      revenueApprovedTotalsPoints.map((p) => [p.year, p.total])
    );
    const actualByYear = new Map(
      revenueActualTotalsPoints.map((p) => [p.year, p.total])
    );

    const commonYears = [...approvedByYear.keys()]
      .filter((y) => actualByYear.has(y))
      .sort((a, b) => a - b);

    if (!commonYears.length) return { labels: [], series: [], groups: [] };

    const labels = commonYears.map((y) => fyLabelFromStartYear(y));

    const series = [
      {
        key: "totalApprovedRevenues",
        label: "Total Approved Revenues",
        data: commonYears.map((y) => approvedByYear.get(y) ?? null),
        group: "revenues",
        defaultVisible: true,
        valueType: "currency",
      },
      {
        key: "totalActualRevenues",
        label: "Total Actual Revenues",
        data: commonYears.map((y) => actualByYear.get(y) ?? null),
        group: "revenues",
        defaultVisible: true,
        valueType: "currency",
      },
    ];

    const groups = [
      {
        key: "totalRevenues",
        label: "Total Revenues",
        groupKey: "revenues",
        seriesKeys: ["totalApprovedRevenues", "totalActualRevenues"],
        defaultVisible: true,
      },
    ];

    return { labels, series, groups };
  }, [revenueApprovedTotalsPoints, revenueActualTotalsPoints]);

  // Pie slices: category breakdown for selected column for the selected year
  const revenuePieItems = useMemo(() => {
    if (!hasAnyData) return [];
    if (!pieColKey) return [];
    if (!tableYear) return [];

    const sectionsObj = yearCacheRef.current.get(tableYear);
    if (!sectionsObj) return [];

    const rows = computeCategoryAggRowsForYear(tableYear, sectionsObj);

    const items = [];

    const addSlice = (label, value, keyHint) => {
      if (typeof value !== "number" || !Number.isFinite(value) || value === 0)
        return;

      const safeKey =
        String(keyHint || label || "")
          .trim()
          .replace(/\s+/g, "_")
          .replace(/[^a-zA-Z0-9_]/g, "") || `slice_${items.length + 1}`;

      items.push({
        key: safeKey,
        label,
        value,
        valueType: "currency",
        group: "other",
      });
    };

    rows.forEach((r, idx) => {
      const lbl = String(r?.label || "").trim();
      if (!lbl) return;
      addSlice(lbl, r?.[pieColKey], `rev_${idx}_${lbl}`);
    });

    items.sort((a, b) => (b.value || 0) - (a.value || 0));
    return items;
  }, [hasAnyData, pieColKey, tableYear, computeCategoryAggRowsForYear]);

  return (
    <TableSearchScope>
    <div className="bg-base-100 shadow-md rounded-lg p-2 mt-6 border border-gray-300">
      <HeaderToggleButton reportNavigation title={TOWN_REPORTS[1].title} controlsId="town-revenue-summary"
        isOpen={!collapsed} onToggle={() => setCollapsed(prev => !prev)} />

      {!collapsed && (
        <div id="town-revenue-summary" className="bg-white p-4 space-y-6">
          {loading && !hasAnyData ? (
            <div className="text-sm text-gray-500">Loading budget summary...</div>
          ) : (
            <>
              {/* TAXES header row with Current Year on the same axis */}
              <div className="flex flex-wrap gap-2 items-center justify-between rounded border border-gray-200 bg-gray-100 px-2 py-1">
                <h3 className="text-lg font-semibold">TAXES</h3>

                {yearOptionsToShow.length > 0 && (
                  <div className="ml-auto flex flex-wrap items-center justify-end gap-2 text-xs">
                    <SectionTableSearch />
                    <span className="font-semibold text-gray-700">
                      Current Year
                    </span>
                    <select
                      className="border rounded px-2 py-1 text-xs bg-white"
                      value={tableYear || ""}
                      onChange={(e) =>
                        setTableYear(
                          e.target.value ? Number(e.target.value) : tableYear
                        )
                      }
                    >
                      {yearOptionsToShow.map((y) => (
                        <option key={y} value={y}>
                          {fyLabelFromStartYear(y)}
                        </option>
                      ))}
                    </select>

                    {isUpdating && (
                      <span className="text-gray-500" aria-live="polite">
                        Updating…
                      </span>
                    )}
                  </div>
                )}
              </div>

              <CommonTable
                title={null}
                columns={columns}
                data={taxDisplayData}
                isEditing={isEditing}
                headerColor="bg-[#dbead7]"
                onChange={handleTaxChange}
                showAggregateTotal={false}
                dataKey={yearKey(0, isEditing ? "adopted" : "actual")}
              />

              <CommonTable
                title="LICENSES & PERMITS"
                columns={columns}
                data={licenseDisplayData}
                isEditing={isEditing}
                headerColor="bg-[#dbead7]"
                onChange={handleLicenseChange}
                showAggregateTotal={false}
                dataKey={yearKey(0, isEditing ? "adopted" : "actual")}
              />

              <CommonTable
                title="INTERGOVERNMENTAL"
                columns={columns}
                data={intergovDisplayData}
                isEditing={isEditing}
                headerColor="bg-[#dbead7]"
                onChange={handleIntergovChange}
                showAggregateTotal={false}
                sectionOrder={INTERGOV_SECTION_ORDER}
                dataKey={yearKey(0, isEditing ? "adopted" : "actual")}
              />

              <CommonTable
                title="CHARGES FOR SERVICES"
                columns={columns}
                data={chargesDisplayData}
                isEditing={isEditing}
                headerColor="bg-[#dbead7]"
                onChange={handleChargesChange}
                showAggregateTotal={false}
                dataKey={yearKey(0, isEditing ? "adopted" : "actual")}
              />

              <CommonTable
                title="OTHER REVENUES"
                columns={columns}
                data={otherDisplayData}
                isEditing={isEditing}
                headerColor="bg-[#dbead7]"
                onChange={handleOtherChange}
                showAggregateTotal={false}
                dataKey={yearKey(0, isEditing ? "adopted" : "actual")}
              />

              <CommonTable
                title="TRANSFERS IN"
                columns={columns}
                data={transfersDisplayData}
                isEditing={isEditing}
                headerColor="bg-[#dbead7]"
                onChange={handleTransfersChange}
                showAggregateTotal={false}
                dataKey={yearKey(0, isEditing ? "adopted" : "actual")}
              />

              {/* GRAND TOTAL REVENUES & TRANSFERS */}
              {grandTotalRow && (
                <CommonTable
                  title=""
                  columns={columns}
                  data={[grandTotalRow]}
                  isEditing={false}
                  headerColor="bg-[#dbead7]"
                  onChange={() => {}}
                  showAggregateTotal={false}
                  dataKey={yearKey(0, "adopted")}
                />
              )}

              {/* Charts */}
              <div className="space-y-6 pt-2">
                {revenueApprovedTotalsChart.labels.length > 0 &&
                  revenueApprovedTotalsChart.series.length > 0 && (
                    <div className="space-y-1">
                      <HeaderToggleButton
                        isOpen={showApprovedTotalsChart}
                        onToggle={() =>
                          setShowApprovedTotalsChart((prev) => !prev)
                        }
                        title="Approved Revenues"
                        ariaLabel="Toggle approved revenues chart"
                        controlsId="revenues-totals-approved-chart"
                        rightSlot={
                          isChartUpdating ? (
                            <span
                              className="text-xs text-gray-500 pr-2"
                              aria-live="polite"
                            >
                              Updating…
                            </span>
                          ) : null
                        }
                      />

                      {showApprovedTotalsChart && (
                        <div id="revenues-totals-approved-chart">
                          <p className="text-xs text-gray-600 mb-1">
                            This chart shows total approved revenues (TOTAL
                            REVENUES) by fiscal year.
                          </p>

                          <LineChartWithToggles
                            labels={revenueApprovedTotalsChart.labels}
                            series={revenueApprovedTotalsChart.series}
                            showTotalWhenNoneSelected={true}
                            totalLabel="Total Approved Revenues"
                            showSeriesToggles={true}
                            showChartTypeSelector={true}
                          />
                        </div>
                      )}
                    </div>
                  )}

                {revenueActualTotalsChart.labels.length > 0 &&
                  revenueActualTotalsChart.series.length > 0 && (
                    <div className="space-y-1">
                      <HeaderToggleButton
                        isOpen={showActualTotalsChart}
                        onToggle={() => setShowActualTotalsChart((prev) => !prev)}
                        title="Actual Revenues"
                        ariaLabel="Toggle actual revenues chart"
                        controlsId="revenues-totals-actual-chart"
                        rightSlot={
                          isChartUpdating ? (
                            <span
                              className="text-xs text-gray-500 pr-2"
                              aria-live="polite"
                            >
                              Updating…
                            </span>
                          ) : null
                        }
                      />

                      {showActualTotalsChart && (
                        <div id="revenues-totals-actual-chart">
                          <p className="text-xs text-gray-600 mb-1">
                            This chart shows total actual revenues by fiscal
                            year (using the same Y+2 / y2_actual anchoring used
                            elsewhere).
                          </p>

                          <LineChartWithToggles
                            labels={revenueActualTotalsChart.labels}
                            series={revenueActualTotalsChart.series}
                            showTotalWhenNoneSelected={true}
                            totalLabel="Total Actual Revenues"
                            showSeriesToggles={true}
                            showChartTypeSelector={true}
                          />
                        </div>
                      )}
                    </div>
                  )}

                {revenueDiffTotalsChart.labels.length > 0 &&
                  revenueDiffTotalsChart.series.length > 0 && (
                    <div className="space-y-1">
                      <HeaderToggleButton
                        isOpen={showDiffTotalsChart}
                        onToggle={() => setShowDiffTotalsChart((prev) => !prev)}
                        title="Actual Spending vs Approved Budget Revenues"
                        ariaLabel="Toggle actual vs approved revenues chart"
                        controlsId="revenues-totals-diff-chart"
                        rightSlot={
                          isChartUpdating ? (
                            <span
                              className="text-xs text-gray-500 pr-2"
                              aria-live="polite"
                            >
                              Updating…
                            </span>
                          ) : null
                        }
                      />

                      {showDiffTotalsChart && (
                        <div id="revenues-totals-diff-chart">
                          <p className="text-xs text-gray-600 mb-1">
                            This chart compares total actual vs total approved
                            revenues for years where both are available.
                          </p>

                          <LineChartWithToggles
                            labels={revenueDiffTotalsChart.labels}
                            series={revenueDiffTotalsChart.series}
                            groups={revenueDiffTotalsChart.groups}
                            showChartTypeSelector={true}
                          />
                        </div>
                      )}
                    </div>
                  )}

                {/* Revenue Breakdown Pie */}
                <div className="space-y-1">
                  <HeaderToggleButton
                    isOpen={showRevenuePie}
                    onToggle={() => setShowRevenuePie((p) => !p)}
                    title="Breakdown of Revenues"
                    ariaLabel="Toggle breakdown of revenues"
                    controlsId="revenues-breakdown-pie"
                    rightSlot={
                      isChartUpdating ? (
                        <span
                          className="text-xs text-gray-500 pr-2"
                          aria-live="polite"
                        >
                          Updating…
                        </span>
                      ) : null
                    }
                  />

                  {showRevenuePie && (
                    <div id="revenues-breakdown-pie">
                      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                        <p className="text-xs text-gray-600">
                          This pie shows how TOTAL REVENUES are split across the
                          major revenue categories for the selected column.
                        </p>

                        {pieColOptions.length > 0 && (
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="font-semibold">Column</span>
                            <select
                              className="border rounded px-2 py-1 text-xs bg-white"
                              value={pieColKey || ""}
                              onChange={(e) => setPieColKey(e.target.value)}
                            >
                              {pieColOptions.map((o) => (
                                <option key={o.key} value={o.key}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>

                      {revenuePieItems.length > 0 ? (
                        <PieChartWithToggles
                          title={null}
                          items={revenuePieItems}
                          percentOf="all"
                          showLegend={false}
                          showSeriesToggles={false}
                          showChartTypeSelector={false}
                          aggregateOthers={false}
                          includeZeroValues={true}
                          showLegendTable={true}
                          legendTablePlacement="right"
                          legendTableMaxHeight="420px"
                          enableHoverPop={true}
                          hoverPopOffset={12}
                          sliceOrder="valueDesc"
                        />
                      ) : (
                        <div className="text-sm text-gray-500">
                          No revenue data available for the selected column.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
    </TableSearchScope>
  );
}

export default SummaryOfRevenues;
